import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useTheme } from '../context/ThemeContext';

function splitDomainsInput(raw) {
  return (raw || '')
    .split(/[\s,]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

function normalizeDomainToken(token) {
  return token.trim().toLowerCase();
}

function isValidBareDomain(domain) {
  // Requirements:
  // - no scheme, no path, no port, no @
  // - must contain at least one dot
  // - labels: [a-z0-9-], no leading/trailing hyphen, 1-63 chars
  // - tld: at least 2 letters
  if (!domain) return false;
  if (domain.includes('://')) return false;
  if (domain.includes('/')) return false;
  if (domain.includes('@')) return false;
  if (domain.includes(':')) return false;
  if (domain.startsWith('.')) return false;
  if (domain.endsWith('.')) return false;
  if (domain.includes('..')) return false;

  const parts = domain.split('.');
  if (parts.length < 2) return false;

  const tld = parts[parts.length - 1];
  if (!/^[a-z]{2,}$/.test(tld)) return false;

  for (const label of parts) {
    if (!label) return false;
    if (label.length > 63) return false;
    if (!/^[a-z0-9-]+$/.test(label)) return false;
    if (label.startsWith('-') || label.endsWith('-')) return false;
  }

  return true;
}

function isValidEmail(email) {
  if (!email) return false;
  if (email.includes('://') || email.includes('/') || email.includes(' ')) return false;
  const at = email.indexOf('@');
  if (at <= 0 || at !== email.lastIndexOf('@')) return false;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  if (!local || local.length > 64) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  return isValidBareDomain(domain);
}

function ItemModal({
  title,
  description,
  mode,
  itemKind = 'domain',
  initialItemsText = '',
  initialReason = '',
  confirmLabel,
  onCancel,
  onConfirm,
  loading,
}) {
  const { theme } = useTheme();
  const [itemsText, setItemsText] = useState(initialItemsText);
  const [reason, setReason] = useState(initialReason);
  const [touched, setTouched] = useState(false);
  const isEmail = itemKind === 'email';
  const itemNoun = isEmail ? 'email' : 'domain';
  const itemNounPlural = isEmail ? 'emails' : 'domains';

  const parsed = useMemo(() => {
    const tokens = splitDomainsInput(itemsText).map(normalizeDomainToken);
    const uniq = Array.from(new Set(tokens));
    const valid = [];
    const invalid = [];
    const validateItem = itemKind === 'email' ? isValidEmail : isValidBareDomain;
    uniq.forEach((d) => (validateItem(d) ? valid.push(d) : invalid.push(d)));
    return { tokens: uniq, valid, invalid };
  }, [itemsText, itemKind]);

  const reasonError = touched && !reason.trim() ? 'Reason is required.' : '';
  const itemsError =
    touched && mode === 'add' && parsed.valid.length === 0
      ? `Enter at least one valid ${itemNoun}${isEmail ? ' (e.g. user@abc.com)' : ' (e.g. abc.com)'}.`
      : '';

  const canSubmit =
    !loading &&
    reason.trim() &&
    (mode === 'remove' ? parsed.valid.length === 1 : parsed.valid.length > 0) &&
    (mode !== 'add' || parsed.invalid.length === 0);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: 12,
          padding: 24,
          width: 'min(720px, 92vw)',
          maxHeight: '85vh',
          overflow: 'auto',
          border: `1px solid ${theme.border}`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <h2 style={{ margin: 0, color: theme.text }}>{title}</h2>
          <button
            onClick={onCancel}
            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: theme.textMuted }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {description && <div style={{ marginTop: 8, color: theme.textSecondary, fontSize: 13 }}>{description}</div>}

        <div style={{ marginTop: 18 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>
            {isEmail ? 'Email(s)' : 'Domain(s)'}
          </label>
          <textarea
            value={itemsText}
            onChange={(e) => setItemsText(e.target.value)}
            onBlur={() => setTouched(true)}
            rows={mode === 'remove' ? 2 : 5}
            placeholder={
              mode === 'remove'
                ? isEmail
                  ? 'user@example.com'
                  : 'example.com'
                : isEmail
                  ? 'user@abc.com, other@xy.com'
                  : 'abc.com, xy.com'
            }
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.inputBackground,
              color: theme.text,
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
            disabled={mode === 'remove'}
          />
          {itemsError ? <div style={{ marginTop: 6, color: theme.danger, fontSize: 12 }}>{itemsError}</div> : null}
          {mode === 'add' && parsed.invalid.length > 0 ? (
            <div style={{ marginTop: 10, background: theme.tableHeader, padding: 10, borderRadius: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: theme.text }}>
                Invalid {itemNounPlural} (fix/remove these)
              </div>
              <div style={{ color: theme.textSecondary, fontSize: 12, lineHeight: 1.6 }}>
                {parsed.invalid.join(', ')}
              </div>
              <div style={{ marginTop: 6, color: theme.textSecondary, fontSize: 12 }}>
                {isEmail ? (
                  <>
                    Use full addresses like <strong>user@abc.com</strong> (no `http://`, no paths).
                  </>
                ) : (
                  <>
                    Use bare domains like <strong>abc.com</strong> (no `http://`, no paths, no `@`).
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: theme.text }}>
            Reason (for audit logs)
          </label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Why are you making this change?"
            style={{
              width: '100%',
              padding: 10,
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.inputBackground,
              color: theme.text,
            }}
          />
          {reasonError ? <div style={{ marginTop: 6, color: theme.danger, fontSize: 12 }}>{reasonError}</div> : null}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.cardBackground,
              color: theme.text,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setTouched(true);
              if (!canSubmit) return;
              onConfirm({ items: parsed.valid, reason: reason.trim() });
            }}
            disabled={!canSubmit}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: canSubmit ? theme.primary : theme.border,
              color: 'white',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 600,
            }}
          >
            {loading ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SignupBlacklist() {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lists, setLists] = useState({
    blacklistedDomains: [],
    disposableDomains: [],
    whitelistedEmails: [],
    emailVerifyWhitelistRedisKey: 'email_verify:whitelist',
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [removeEmailTarget, setRemoveEmailTarget] = useState(null);
  const [blacklistSearch, setBlacklistSearch] = useState('');
  const [disposableSearch, setDisposableSearch] = useState('');
  const [whitelistSearch, setWhitelistSearch] = useState('');
  const [environment, setEnvironment] = useState('production');

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/api/signup-blacklist/email-domain-lists?environment=${encodeURIComponent(environment)}`);
      const data = res.data?.data || {};
      setLists({
        blacklistedDomains: Array.isArray(data.blacklistedDomains) ? data.blacklistedDomains : [],
        disposableDomains: Array.isArray(data.disposableDomains) ? data.disposableDomains : [],
        whitelistedEmails: Array.isArray(data.whitelistedEmails) ? data.whitelistedEmails : [],
        emailVerifyWhitelistRedisKey: data.emailVerifyWhitelistRedisKey || 'email_verify:whitelist',
      });
    } catch (e) {
      console.error('Failed to fetch email domain lists:', e);
      setError(e.response?.data?.error || 'Failed to fetch lists');
    } finally {
      setLoading(false);
    }
  }, [environment]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const sorted = useMemo(() => {
    const normSort = (arr) => [...arr].map((d) => String(d)).sort((a, b) => a.localeCompare(b));
    return {
      blacklistedDomains: normSort(lists.blacklistedDomains),
      disposableDomains: normSort(lists.disposableDomains),
      whitelistedEmails: normSort(lists.whitelistedEmails),
    };
  }, [lists]);

  const filtered = useMemo(() => {
    const q1 = blacklistSearch.trim().toLowerCase();
    const q2 = disposableSearch.trim().toLowerCase();
    const q3 = whitelistSearch.trim().toLowerCase();

    return {
      blacklistedDomains: q1 ? sorted.blacklistedDomains.filter((d) => d.toLowerCase().includes(q1)) : sorted.blacklistedDomains,
      disposableDomains: q2 ? sorted.disposableDomains.filter((d) => d.toLowerCase().includes(q2)) : sorted.disposableDomains,
      whitelistedEmails: q3 ? sorted.whitelistedEmails.filter((d) => d.toLowerCase().includes(q3)) : sorted.whitelistedEmails,
    };
  }, [sorted, blacklistSearch, disposableSearch, whitelistSearch]);

  const updateBlocklist = async ({ action, domains, reason }) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.post(`/api/signup-blacklist/email-domain-blocklist?environment=${encodeURIComponent(environment)}`, { action, domains, reason });
      setSuccess(`${action === 'add' ? 'Added' : 'Removed'} ${domains.length} domain(s) successfully.`);
      await fetchLists();
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      console.error('Failed to update blocklist:', e);
      setError(e.response?.data?.error || 'Failed to update blocklist');
    } finally {
      setSaving(false);
    }
  };

  const updateWhitelist = async ({ action, emails, reason }) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.post(`/api/signup-blacklist/email-verify-whitelist?environment=${encodeURIComponent(environment)}`, { action, emails, reason });
      setSuccess(`${action === 'add' ? 'Added' : 'Removed'} ${emails.length} email(s) successfully.`);
      await fetchLists();
      setTimeout(() => setSuccess(''), 3500);
    } catch (e) {
      console.error('Failed to update whitelist:', e);
      setError(e.response?.data?.error || 'Failed to update whitelist');
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    backgroundColor: theme.cardBackground,
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: 18,
  };

  const listBoxStyle = {
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.tableHeader,
    borderRadius: 10,
    height: 420,
    overflow: 'auto',
    padding: 12,
  };

  return (
    <div style={{ padding: 30, maxWidth: 1200, margin: '0 auto', color: theme.text }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: theme.text }}>🚫 Signup BlackList</h1>
          <div style={{ marginTop: 6, color: theme.textSecondary, fontSize: 13 }}>
            Manage signup restrictions (blacklisted + disposable domains) and EmailListVerify whitelist.
          </div>

          {/* Environment Toggle (left-aligned) */}
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: theme.textSecondary, fontWeight: 700 }}>Environment</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setEnvironment('production')}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: `1px solid ${theme.border}`,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: environment === 'production' ? 800 : 600,
                  backgroundColor: environment === 'production' ? theme.primary : theme.cardBackground,
                  color: environment === 'production' ? 'white' : theme.text,
                }}
              >
                Production
              </button>
              <button
                onClick={() => setEnvironment('staging')}
                disabled={saving}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: `1px solid ${theme.border}`,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: environment === 'staging' ? 800 : 600,
                  backgroundColor: environment === 'staging' ? theme.primary : theme.cardBackground,
                  color: environment === 'staging' ? 'white' : theme.text,
                }}
              >
                Staging
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={fetchLists}
            disabled={loading || saving}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.cardBackground,
              color: theme.text,
              cursor: loading || saving ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error ? (
        <div style={{ ...cardStyle, borderColor: theme.danger, backgroundColor: theme.name === 'dark' ? '#5c2a2a' : '#f8d7da', color: theme.name === 'dark' ? '#ffaaaa' : '#721c24', marginBottom: 14 }}>
          {error}
        </div>
      ) : null}

      {success ? (
        <div style={{ ...cardStyle, borderColor: '#28a745', backgroundColor: theme.name === 'dark' ? '#1f3b2a' : '#d4edda', color: theme.name === 'dark' ? '#b7ffcf' : '#155724', marginBottom: 14 }}>
          {success}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 16,
        }}
      >
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Blacklisted domains</div>
              <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
                We can add domains to this list to block them from signup.
              </div>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              disabled={loading || saving}
              style={{
                padding: '9px 12px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: theme.primary,
                color: 'white',
                cursor: loading || saving ? 'not-allowed' : 'pointer',
                fontWeight: 700,
              }}
            >
              + Add Domain to Blacklist
            </button>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input
              value={blacklistSearch}
              onChange={(e) => setBlacklistSearch(e.target.value)}
              placeholder="Search domain…"
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
              }}
            />
            <div style={{ fontSize: 12, color: theme.textSecondary, whiteSpace: 'nowrap' }}>
              {filtered.blacklistedDomains.length}/{sorted.blacklistedDomains.length}
            </div>
          </div>

          <div style={listBoxStyle}>
            {loading ? (
              <div style={{ color: theme.textSecondary, padding: 12 }}>Loading…</div>
            ) : filtered.blacklistedDomains.length === 0 ? (
              <div style={{ color: theme.textSecondary, padding: 12, fontStyle: 'italic' }}>No blacklisted domains</div>
            ) : (
              filtered.blacklistedDomains.map((d) => (
                <div
                  key={d}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 10px',
                    borderRadius: 8,
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.border}`,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 13 }}>
                    {d}
                  </div>
                  <button
                    onClick={() => setRemoveTarget(d)}
                    disabled={saving}
                    style={{
                      padding: '7px 10px',
                      borderRadius: 8,
                      border: `1px solid ${theme.danger}`,
                      backgroundColor: theme.cardBackground,
                      color: theme.danger,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                      fontSize: 12,
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Disposable domains</div>
            <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
              We block these domains from signup. These are powered by https://github.com/disposable/disposable. 
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input
              value={disposableSearch}
              onChange={(e) => setDisposableSearch(e.target.value)}
              placeholder="Search domain…"
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: 8,
                border: `1px solid ${theme.border}`,
                backgroundColor: theme.inputBackground,
                color: theme.text,
              }}
            />
            <div style={{ fontSize: 12, color: theme.textSecondary, whiteSpace: 'nowrap' }}>
              {filtered.disposableDomains.length}/{sorted.disposableDomains.length}
            </div>
          </div>

          <div style={listBoxStyle}>
            {loading ? (
              <div style={{ color: theme.textSecondary, padding: 12 }}>Loading…</div>
            ) : filtered.disposableDomains.length === 0 ? (
              <div style={{ color: theme.textSecondary, padding: 12, fontStyle: 'italic' }}>No disposable domains</div>
            ) : (
              filtered.disposableDomains.map((d) => (
                <div
                  key={d}
                  style={{
                    padding: '10px 10px',
                    borderRadius: 8,
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.border}`,
                    marginBottom: 10,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: 13,
                  }}
                >
                  {d}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>Email verify whitelist</div>
            <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2 }}>
              Full email addresses that skip EmailListVerify during signup/login. Does not bypass disposable or blacklisted domains.
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 12,
                color: theme.textSecondary,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              Redis set: <strong>{environment}:{lists.emailVerifyWhitelistRedisKey || 'email_verify:whitelist'}</strong>
            </div>
          </div>
          <button
            onClick={() => setShowAddEmailModal(true)}
            disabled={loading || saving}
            style={{
              padding: '9px 12px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: theme.primary,
              color: 'white',
              cursor: loading || saving ? 'not-allowed' : 'pointer',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            + Add Email to Whitelist
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <input
            value={whitelistSearch}
            onChange={(e) => setWhitelistSearch(e.target.value)}
            placeholder="Search email…"
            style={{
              flex: 1,
              padding: '10px 12px',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.inputBackground,
              color: theme.text,
            }}
          />
          <div style={{ fontSize: 12, color: theme.textSecondary, whiteSpace: 'nowrap' }}>
            {filtered.whitelistedEmails.length}/{sorted.whitelistedEmails.length}
          </div>
        </div>

        <div style={{ ...listBoxStyle, height: 280 }}>
          {loading ? (
            <div style={{ color: theme.textSecondary, padding: 12 }}>Loading…</div>
          ) : filtered.whitelistedEmails.length === 0 ? (
            <div style={{ color: theme.textSecondary, padding: 12, fontStyle: 'italic' }}>No whitelisted emails</div>
          ) : (
            filtered.whitelistedEmails.map((email) => (
              <div
                key={email}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 10px',
                  borderRadius: 8,
                  backgroundColor: theme.cardBackground,
                  border: `1px solid ${theme.border}`,
                  marginBottom: 10,
                }}
              >
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace', fontSize: 13 }}>
                  {email}
                </div>
                <button
                  onClick={() => setRemoveEmailTarget(email)}
                  disabled={saving}
                  style={{
                    padding: '7px 10px',
                    borderRadius: 8,
                    border: `1px solid ${theme.danger}`,
                    backgroundColor: theme.cardBackground,
                    color: theme.danger,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddModal ? (
        <ItemModal
          title="Add domains to blacklist"
          description="Enter one or more domains, separated by comma or whitespace. Example: abc.com xy.com"
          mode="add"
          itemKind="domain"
          confirmLabel="Add to blacklist"
          onCancel={() => setShowAddModal(false)}
          onConfirm={async ({ items, reason }) => {
            await updateBlocklist({ action: 'add', domains: items, reason });
            setShowAddModal(false);
          }}
          loading={saving}
        />
      ) : null}

      {removeTarget ? (
        <ItemModal
          title="Remove domain from blacklist"
          description={`This will remove ${removeTarget} from the blacklist.`}
          mode="remove"
          itemKind="domain"
          initialItemsText={removeTarget}
          confirmLabel="Remove"
          onCancel={() => setRemoveTarget(null)}
          onConfirm={async ({ items, reason }) => {
            await updateBlocklist({ action: 'remove', domains: items, reason });
            setRemoveTarget(null);
          }}
          loading={saving}
        />
      ) : null}

      {showAddEmailModal ? (
        <ItemModal
          title="Add emails to verify whitelist"
          description="Enter one or more full email addresses, separated by comma or whitespace. These skip EmailListVerify only."
          mode="add"
          itemKind="email"
          confirmLabel="Add to whitelist"
          onCancel={() => setShowAddEmailModal(false)}
          onConfirm={async ({ items, reason }) => {
            await updateWhitelist({ action: 'add', emails: items, reason });
            setShowAddEmailModal(false);
          }}
          loading={saving}
        />
      ) : null}

      {removeEmailTarget ? (
        <ItemModal
          title="Remove email from verify whitelist"
          description={`This will remove ${removeEmailTarget} from ${environment}:${lists.emailVerifyWhitelistRedisKey || 'email_verify:whitelist'}.`}
          mode="remove"
          itemKind="email"
          initialItemsText={removeEmailTarget}
          confirmLabel="Remove"
          onCancel={() => setRemoveEmailTarget(null)}
          onConfirm={async ({ items, reason }) => {
            await updateWhitelist({ action: 'remove', emails: items, reason });
            setRemoveEmailTarget(null);
          }}
          loading={saving}
        />
      ) : null}
    </div>
  );
}

